import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { resourcesService } from '../../services/resources';
import { FaArrowLeft, FaCalendar, FaTag, FaIndustry } from 'react-icons/fa';
import PublicLayout from '../../components/PublicLayout';
import './ResourceViewer.css';

export default function ResourceViewer() {
  const { s3Key } = useParams();
  const [resource, setResource] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchResource = async () => {
      try {
        setLoading(true);
        
        // Add validation
        if (!s3Key || s3Key === 'undefined') {
          throw new Error('Invalid resource key');
        }
        
        // Change from atob to decodeURIComponent
        const decodedS3Key = decodeURIComponent(s3Key);
        const response = await resourcesService.getResourceContent(decodedS3Key);
        
        if (!response.data || !response.data.resource) {
          throw new Error('Invalid resource data');
        }
        
        setResource(response.data.resource);
        setContent(response.data.content);
      } catch (err) {
        console.error('Error fetching resource:', err);
        setError(err.message || 'Failed to load resource content');
      } finally {
        setLoading(false);
      }
    };
    fetchResource();
  }, [s3Key]);

  const getTypeColor = (type) => {
    const colors = {
      'whitepaper': '#3b9efc',
      'blog article': '#28a745',
      'case study': '#ffc107',
      'use case': '#6f42c1'
    };
    return colors[type] || '#6c757d';
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="resource-viewer">
          <div className="loading">Loading resource...</div>
        </div>
      </PublicLayout>
    );
  }

  if (error || !resource) {
    return (
      <PublicLayout>
        <div className="resource-viewer">
          <div className="error">
            <h2>Resource Not Found</h2>
            <p>{error || 'The requested resource could not be found.'}</p>
            <Link to="/resources" className="btn btn-primary">
              <FaArrowLeft /> Back to Resources
            </Link>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="resource-viewer">
        <div className="resource-header">
          <Link to="/resources" className="back-link">
            <FaArrowLeft /> Back to Resources
          </Link>
          
          <div className="resource-meta">
            <span 
              className="resource-type" 
              style={{ backgroundColor: getTypeColor(resource.type) }}
            >
              {resource.type}
            </span>
            <span className="resource-date">
              <FaCalendar />
              {new Date(resource.date).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long',
                day: 'numeric'
              })}
            </span>
            {resource.category && (
              <span className="resource-category">
                <FaTag />
                {resource.category}
              </span>
            )}
            {resource.industry && (
              <span className="resource-industry">
                <FaIndustry />
                {resource.industry}
              </span>
            )}
          </div>
        </div>

        <article className="resource-content">
          <div 
            className="markdown-content"
            dangerouslySetInnerHTML={{ __html: content.html }}
          />
        </article>

        <div className="resource-footer">
          <Link to="/resources" className="btn btn-secondary">
            <FaArrowLeft /> Back to Resources
          </Link>
        </div>
      </div>
    </PublicLayout>
  );
}